/**
 * Pre-defined theme designs for tenant customization
 * Each theme includes color palette, layout preferences, and styling options
 */

export const PREDEFINED_THEMES = {
  modern: {
    id: 'modern',
    name: 'Modern',
    description: 'Clean and contemporary design with vibrant colors',
    colors: {
      primary: '#667eea',
      secondary: '#764ba2',
      accent: '#FFC421',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      neutral: '#f3f4f6',
      dark: '#1f2937',
      text: '#111827',
      textMuted: '#6b7280',
    },
    typography: {
      headingFont: 'Manrope',
      bodyFont: 'Inter',
      headingWeight: 700,
      bodyWeight: 400,
    },
    spacing: {
      compact: 'normal',
      cardRadius: '12px',
      buttonRadius: '8px',
    },
    layout: {
      sidebarPosition: 'left',
      headerStyle: 'gradient',
      cardStyle: 'elevated',
      spacing: 'comfortable',
    },
  },

  minimal: {
    id: 'minimal',
    name: 'Minimal',
    description: 'Minimalist design with focus on content',
    colors: {
      primary: '#000000',
      secondary: '#404040',
      accent: '#ea580c',
      success: '#059669',
      warning: '#d97706',
      error: '#dc2626',
      neutral: '#fafafa',
      dark: '#1a1a1a',
      text: '#000000',
      textMuted: '#525252',
    },
    typography: {
      headingFont: 'Inter',
      bodyFont: 'Inter',
      headingWeight: 600,
      bodyWeight: 400,
    },
    spacing: {
      compact: 'compact',
      cardRadius: '4px',
      buttonRadius: '4px',
    },
    layout: {
      sidebarPosition: 'left',
      headerStyle: 'flat',
      cardStyle: 'bordered',
      spacing: 'compact',
    },
  },

  elegant: {
    id: 'elegant',
    name: 'Elegant',
    description: 'Premium and sophisticated styling',
    colors: {
      primary: '#1e293b',
      secondary: '#334155',
      accent: '#d4af37',
      success: '#06b6d4',
      warning: '#84cc16',
      error: '#f43f5e',
      neutral: '#f8fafc',
      dark: '#0f172a',
      text: '#0f172a',
      textMuted: '#64748b',
    },
    typography: {
      headingFont: 'Georgia',
      bodyFont: 'Georgia',
      headingWeight: 700,
      bodyWeight: 400,
    },
    spacing: {
      compact: 'spacious',
      cardRadius: '16px',
      buttonRadius: '8px',
    },
    layout: {
      sidebarPosition: 'left',
      headerStyle: 'gradient',
      cardStyle: 'elevated',
      spacing: 'spacious',
    },
  },

  vibrant: {
    id: 'vibrant',
    name: 'Vibrant',
    description: 'Bold and energetic color scheme',
    colors: {
      primary: '#ec4899',
      secondary: '#a855f7',
      accent: '#06b6d4',
      success: '#22c55e',
      warning: '#eab308',
      error: '#ff6b6b',
      neutral: '#fdf2f8',
      dark: '#1e1b4b',
      text: '#1e1b4b',
      textMuted: '#71717a',
    },
    typography: {
      headingFont: 'Manrope',
      bodyFont: 'Inter',
      headingWeight: 700,
      bodyWeight: 500,
    },
    spacing: {
      compact: 'comfortable',
      cardRadius: '14px',
      buttonRadius: '8px',
    },
    layout: {
      sidebarPosition: 'left',
      headerStyle: 'gradient',
      cardStyle: 'elevated',
      spacing: 'comfortable',
    },
  },

  sunset: {
    id: 'sunset',
    name: 'Sunset',
    description: 'Warm and inviting color palette',
    colors: {
      primary: '#ea580c',
      secondary: '#f97316',
      accent: '#fbbf24',
      success: '#84cc16',
      warning: '#f59e0b',
      error: '#dc2626',
      neutral: '#fffbeb',
      dark: '#7c2d12',
      text: '#7c2d12',
      textMuted: '#92400e',
    },
    typography: {
      headingFont: 'Manrope',
      bodyFont: 'Inter',
      headingWeight: 700,
      bodyWeight: 400,
    },
    spacing: {
      compact: 'comfortable',
      cardRadius: '12px',
      buttonRadius: '8px',
    },
    layout: {
      sidebarPosition: 'left',
      headerStyle: 'solid',
      cardStyle: 'elevated',
      spacing: 'comfortable',
    },
  },

  ocean: {
    id: 'ocean',
    name: 'Ocean',
    description: 'Cool and professional blue tones',
    colors: {
      primary: '#0369a1',
      secondary: '#0284c7',
      accent: '#06b6d4',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      neutral: '#f0f9ff',
      dark: '#082f49',
      text: '#082f49',
      textMuted: '#0c4a6e',
    },
    typography: {
      headingFont: 'Inter',
      bodyFont: 'Inter',
      headingWeight: 700,
      bodyWeight: 400,
    },
    spacing: {
      compact: 'comfortable',
      cardRadius: '12px',
      buttonRadius: '8px',
    },
    layout: {
      sidebarPosition: 'left',
      headerStyle: 'gradient',
      cardStyle: 'elevated',
      spacing: 'comfortable',
    },
  },

  forest: {
    id: 'forest',
    name: 'Forest',
    description: 'Natural and earthy tones',
    colors: {
      primary: '#15803d',
      secondary: '#16a34a',
      accent: '#22c55e',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#dc2626',
      neutral: '#f0fdf4',
      dark: '#052e16',
      text: '#052e16',
      textMuted: '#166534',
    },
    typography: {
      headingFont: 'Manrope',
      bodyFont: 'Inter',
      headingWeight: 700,
      bodyWeight: 400,
    },
    spacing: {
      compact: 'comfortable',
      cardRadius: '12px',
      buttonRadius: '8px',
    },
    layout: {
      sidebarPosition: 'left',
      headerStyle: 'solid',
      cardStyle: 'bordered',
      spacing: 'comfortable',
    },
  },
};

/**
 * Apply theme colors to CSS variables
 * @param {Object} theme - Theme object with colors property
 */
export const applyTheme = (theme) => {
  if (!theme || !theme.colors) return;

  const root = document.documentElement;
  
  // Set CSS custom properties for colors
  Object.entries(theme.colors).forEach(([key, value]) => {
    const cssVariableName = `--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
    root.style.setProperty(cssVariableName, value);
  });

  // Set typography CSS variables
  if (theme.typography) {
    Object.entries(theme.typography).forEach(([key, value]) => {
      const cssVariableName = `--typography-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
      root.style.setProperty(cssVariableName, String(value));
    });
  }

  // Set spacing CSS variables
  if (theme.spacing) {
    Object.entries(theme.spacing).forEach(([key, value]) => {
      const cssVariableName = `--spacing-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
      root.style.setProperty(cssVariableName, value);
    });
  }

  // Store current theme in localStorage
  localStorage.setItem('selectedTheme', JSON.stringify(theme));
};

/**
 * Get the currently applied theme or return the theme object
 */
export const getCurrentTheme = () => {
  const saved = localStorage.getItem('selectedTheme');
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (e) {
      return PREDEFINED_THEMES.modern;
    }
  }
  return PREDEFINED_THEMES.modern;
};

/**
 * Get a theme by ID
 */
export const getThemeById = (themeId) => {
  return PREDEFINED_THEMES[themeId] || PREDEFINED_THEMES.modern;
};

/**
 * Get all available theme IDs
 */
export const getAvailableThemeIds = () => {
  return Object.keys(PREDEFINED_THEMES);
};

/**
 * Create CSS variables for theme colors (for use in styled components)
 */
export const getThemeCSS = (theme = getCurrentTheme()) => {
  if (!theme.colors) return '';

  const cssLines = Object.entries(theme.colors).map(([key, value]) => {
    const cssVariableName = `--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
    return `${cssVariableName}: ${value};`;
  }).join('\n');

  return `:root { ${cssLines} }`;
};
